import { FormEvent, useState } from 'react';
import { Box, Input, useToast } from '@chakra-ui/react';
import { adicionarLesson } from '../../services/api';
import { CampoForm } from '../../components/CampoForm';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { vazio } from '../../utils/validacao';
import { toBase64, uploadWithBase64 } from '../../utils/imageHelper';

type DrawerCreateProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	bookId: string;
	afterClose: () => void;
};

type Campo = 'nome' | 'pdf' | 'capa';

export function DrawerCreate({ isOpen, onClose, afterClose, bookId }: DrawerCreateProps) {
	const toast = useToast();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);

	const [ nome, setNome ] = useState('');
	const [ pdf, setPdf ] = useState('');
	const [ capa, setCapa ] = useState('');

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(nome)) encontrados.nome = 'Nome da lesson é obrigatório!';
		if (!pdf) encontrados.pdf = 'Selecione o PDF da lesson!';
		if (!capa) encontrados.capa = 'Capa é obrigatória!';

		return encontrados;
	};

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setLoading(true);
		Promise.all([ uploadWithBase64(capa), uploadWithBase64(pdf) ])
			.then(([ thumbnail, canvaUrl ]) =>
				adicionarLesson({
					bookId,
					nome: nome.trim(),
					canvaUrl,
					thumbnail
				})
			)
			.then(() => {
				setLoading(false);
				toast({
					title: `Lesson adicionada com sucesso!`,
					status: 'success',
					isClosable: true
				});
				resetForm();
				onClose();
				afterClose();
			})
			// Sem este catch uma falha de upload deixava o spinner girando para sempre.
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível adicionar a lesson.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const resetForm = () => {
		setNome('');
		setPdf('');
		setCapa('');
		limparTudo();
	};

	const handleSelectedFile = async (files: FileList | null) => {
		if (files && files[0] && files[0].size < 10000000) {
			const picBase64 = await toBase64(files[0]);
			setCapa(`${picBase64}`);
			limparErro('capa');
		} else {
			toast({
				title: `Foto muito pesada, selecione outra!`,
				status: 'warning',
				isClosable: true
			});
		}
	};

	const handleSelectedFilePdf = async (files: FileList | null) => {
		if (files && files[0]) {
			const arquivoBase64 = await toBase64(files[0]);
			setPdf(`${arquivoBase64}`);
			limparErro('pdf');
		}
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<DrawerForm
			isOpen={isOpen}
			onClose={handleFechar}
			titulo="Nova Lesson"
			formId="form-nova-lesson"
			loading={loading}
			loadingLabel="Salvando..."
			onSubmit={handleSalvar}
		>
			<CampoForm label="Nome da Lesson" htmlFor="nome" erro={erros.nome}>
				<Input
					id="nome"
					placeholder="Insira o Nome da Lesson"
					onChange={(e) => {
						setNome(e.target.value);
						limparErro('nome');
					}}
					value={nome}
				/>
			</CampoForm>

			<CampoForm label="Pdf" htmlFor="pdf" erro={erros.pdf}>
				<Input
					id="pdf"
					type="file"
					accept="application/pdf"
					paddingTop="4px"
					onChange={(files) => handleSelectedFilePdf(files.target.files)}
				/>
			</CampoForm>

			<Box
				width="100%"
				height="150px"
				borderWidth="1px"
				borderColor="gray.200"
				borderRadius="10px"
				bgPosition="center"
				bgSize="cover"
				bgImage={capa}
			/>

			<CampoForm label="Capa" htmlFor="capa" erro={erros.capa}>
				<Input
					id="capa"
					type="file"
					accept="image/*"
					paddingTop="4px"
					onChange={(files) => handleSelectedFile(files.target.files)}
				/>
			</CampoForm>
		</DrawerForm>
	);
}
